/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import {
  CohortPlaylist,
  CreateUserGroups,
  GroupTypeReconciliationSummary,
  UserGroupMember,
  UserGroupTypeDetails,
  UserGroupTypeSettingsRequest,
  UserGroupTypeSettingsResponse,
  UserGroupUploadResult,
  UserGroups,
} from '../../../../shared/models/admin/group/user-groups.model';
import { AccountInvitationsSummary } from '../../../../shared/models/admin/members.model';
import { Page } from '../../../../shared/models/page';
import { ObservableResult } from '../../../../shared/store';
import { UserGroupsDataService } from './groups-data.service';

@Injectable()
export class MockUserGroupsDataService implements UserGroupsDataService {
  private userGroup: UserGroups = {
    _id: '0',
    title: 'GROUP title',
    organizationId: '0',
    memberCount: 10,
    type: 'TYPE',
    lastUpdatedOn: new Date(),
  };

  private groupTypes: UserGroupTypeDetails[] = [
    { type: 'Department', cardinality: 'MULTI', retainAccessWhenPast: true, groupCount: 4, configured: false },
    { type: 'YearOfStudy', cardinality: 'SINGLE', retainAccessWhenPast: true, groupCount: 3, configured: true },
  ];

  loadUserGroupsMembers(userGroupUid: string, page: number, size: number, term?: string): ObservableResult<Page<UserGroupMember>> {
    return ObservableResult.ofSuccess();
  }

  searchUserGroups(
    page: number,
    size: number,
    term?: string,
    playlistUid?: string,
    types?: string[],
    clientId?: string,
    isActive?: boolean,
    playlistId?: string,
  ): ObservableResult<Page<UserGroups>> {
    return ObservableResult.ofSuccess({
      currentPageNumber: 0,
      pageSize: 1,
      totalNumberOfElement: 1,
      content: [this.userGroup],
    }).pipe(delay(2000));
  }

  bulkUploadUsers(csvFile: File, groupId: string): ObservableResult<UserGroupUploadResult> {
    return from(csvFile.text()).pipe(
      switchMap((text) => {
        const emails = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        const processedUsers: string[] = [];
        const invitedUsers: string[] = [];
        const failedUsers: string[] = [];

        emails.forEach((email, index) => {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            failedUsers.push(email);
          } else if (index % 2 === 0) {
            processedUsers.push(email);
          } else {
            invitedUsers.push(email);
          }
        });

        return ObservableResult.ofSuccess({
          processedUsers,
          invitedUsers,
          failedUsers,
          successCount: processedUsers.length,
          failureCount: failedUsers.length,
        });
      }),
      delay(1000),
    );
  }

  getGroupInvitationsSummary(groupUid: string): ObservableResult<AccountInvitationsSummary> {
    return ObservableResult.ofSuccess({ total: 3, pending: 2, accepted: 1, expired: 0 });
  }

  loadSystemGroups(): ObservableResult<string[]> {
    return ObservableResult.ofSuccess();
  }

  loadReviewerGroupTypes(playlistUid?: string): ObservableResult<string[]> {
    return ObservableResult.ofSuccess();
  }

  createUserGroup(request: CreateUserGroups): ObservableResult<UserGroups> {
    return ObservableResult.ofSuccess();
  }

  updateUserGroup(userGroupUid: string, request: CreateUserGroups): ObservableResult<UserGroups> {
    return ObservableResult.ofSuccess();
  }

  loadUserGroup(userGroupUid: string, playlistUid?: string): ObservableResult<UserGroups> {
    return ObservableResult.ofSuccess();
  }

  getUserGroupsByType(type: string, playlistUid?: string): ObservableResult<UserGroups[]> {
    return ObservableResult.ofSuccess();
  }

  getAllUserGroups(playlistUid?: string): ObservableResult<UserGroups[]> {
    return ObservableResult.ofSuccess();
  }

  assignMultipleGroupsToMultipleUsers(groupUids: string[], userUids: string[]): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  removeMultipleGroupsFromMultipleUsers(groupUids: string[], userUids: string[]): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  loadCohortPlaylists(term?: string): ObservableResult<CohortPlaylist[]> {
    return ObservableResult.ofSuccess([]);
  }

  loadGroupTypeSettings(): ObservableResult<UserGroupTypeDetails[]> {
    return ObservableResult.ofSuccess([...this.groupTypes]);
  }

  previewGroupTypeSettings(type: string, request: UserGroupTypeSettingsRequest): ObservableResult<GroupTypeReconciliationSummary> {
    return ObservableResult.ofSuccess(this.reconciliationFor(request)).pipe(delay(300));
  }

  updateGroupTypeSettings(type: string, request: UserGroupTypeSettingsRequest): ObservableResult<UserGroupTypeSettingsResponse> {
    const existing = this.groupTypes.find((groupType) => groupType.type === type);
    if (!existing) {
      return ObservableResult.ofError('Group type could not be saved');
    }
    const settings: UserGroupTypeDetails = { ...existing, ...request, configured: true };
    this.groupTypes = this.groupTypes.map((groupType) => (groupType.type === type ? settings : groupType));
    return ObservableResult.ofSuccess({ settings, reconciliation: this.reconciliationFor(request) });
  }

  private reconciliationFor(request: UserGroupTypeSettingsRequest): GroupTypeReconciliationSummary {
    return request.cardinality === 'SINGLE'
      ? { reconciledUsers: 3, endedMemberships: 4, reStampedMemberships: 0 }
      : { reconciledUsers: 0, endedMemberships: 0, reStampedMemberships: 0 };
  }
}
